import { useQueryClient } from '@tanstack/react-query'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import { z } from 'zod'
import { createFetcher } from '../../libs/next/api/jsonApiHandler'
import type { API } from '../../pages/api/auth/[handler]'
import { Credentials, SignupCredentials } from '../data/Credentials'
import { useAuth, useIsSignedIn } from '../hooks/useAuth'
import { usePlanteeUser } from '../hooks/usePlanteeUser'
import { formDataFromEvent } from '../utils/formDataFromEvent'
import styles from './AuthTest.module.sass'
import { Button } from './Buttons'
import { Input } from './Input'

export const AuthTest = (props: { signUp?: boolean }) => {
	const plantee = usePlanteeUser()

	const signedIn = useIsSignedIn()

	const router = useRouter()
	const locale = router.locale
	const api = createFetcher<API>('/api/auth/[handler]')
	const queryClient = useQueryClient()

	const auth = useAuth()

	const [email, setEmail] = React.useState<string>('')
	const [nick, setNick] = React.useState<string>('')
	const [signUpStatus, setSignUpStatus] = React.useState<string>('')

	React.useEffect(() => {
		if (signedIn) {
			router.push('/')
		}
	}, [router, signedIn])

	return (
		<div className={styles.wrapper}>
			{!signedIn && !props.signUp && (
				<div>
					<h2>{locale === 'cs' ? 'Přihlásit se' : 'Log in'}</h2>

					<form
						action=""
						method="POST"
						onSubmit={(e) => {
							e.preventDefault()
							console.log('submit')
							console.log(formDataFromEvent(e))
							plantee.auth.signIn.mutate(Credentials.parse(formDataFromEvent(e)))
						}}>
						<fieldset
							disabled={plantee.auth.signIn.isLoading}
							style={{ opacity: plantee.auth.signIn.isLoading ? 0.5 : 1 }}>
							<Input
								id="username"
								name="username"
								type="email"
								required
								iconDecoration="pencil"
								label={{
									isLabel: true,
									icon: 'user',
									text: locale === 'cs' ? 'E-mail' : 'E-mail',
								}}
								value={email}
								onChange={(e) => {
									setEmail(e.currentTarget.value)
								}}
							/>
							<br></br>
							<br></br>
							<br></br>
							<Input
								id="password"
								name="password"
								type="password"
								required
								label={{
									isLabel: true,
									icon: 'lock',
									text: locale === 'cs' ? 'Heslo' : 'Password',
								}}
							/>
							<p style={{ color: '#d73a4a' }}>
								{plantee.auth.signIn.error
									? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
									  //@ts-ignore
									  (plantee.auth.signIn.error.message as string)
									: ''}
							</p>
							<br></br>
							<Button type="submit" fill="green600">
								{locale === 'cs' ? 'Přihlásit se' : 'Log in'}
							</Button>
							<p>
								{locale === 'cs'
									? 'Ještě nemáš účet? '
									: 'Do not have an account yet? You might want to '}
								<span style={{ textDecoration: 'underline' }}>
									<Link href="/signup">{locale === 'cs' ? 'Zaregistrovat se' : 'Sign up'}</Link>
								</span>
							</p>
							<p>
								{locale === 'cs'
									? 'Zapomněl jsi heslo? '
									: 'Forgot your password? You might want to '}
								<span style={{ textDecoration: 'underline' }}>
									<button
										className={styles.textButton}
										type="button"
										onClick={(e) => {
											e.preventDefault()
											console.log(email)
											if (email) {
												auth.passwordReset.mutate(
													{ email: email },
													{
														onSuccess(data) {
															const response = data as { message?: string; statusCode: string }
															if (
																response.message === 'User has no ext.' ||
																response.statusCode === '404'
															) {
																setSignUpStatus('User with this e-mail does not exist.')
															}
															if (response.statusCode === '200') {
																console.log('vv')
																setSignUpStatus(
																	'Password reset successfull please check your inbox'
																)
															} else {
																if (response.message) {
																	setSignUpStatus(response.message)
																} else {
																	setSignUpStatus(
																		'Password reset was not successful, please try again'
																	)
																}
															}
														},
													}
												)
											} else {
												setSignUpStatus(
													'Please fill in email address in order to reset your password'
												)
											}
										}}>
										{locale === 'cs' ? 'Obnovit heslo' : 'reset your password'}
									</button>
								</span>
							</p>
						</fieldset>
					</form>
				</div>
			)}

			{!signedIn && signUpStatus !== 'signedUp' && props.signUp && (
				<div>
					<h2>{locale === 'cs' ? 'Založení účtu' : 'Sign up'}</h2>
					<form
						className={styles.signUpForm}
						action=""
						method="POST"
						onSubmit={async (e) => {
							e.preventDefault()
							const pass = formDataFromEvent(e).password
							if (pass.length >= 8) {
								await api('signUp', SignupCredentials.parse(formDataFromEvent(e))).then((res) => {
									queryClient.invalidateQueries(['auth'])
									console.log(res)
									if (res.status && res.status === 'signedUp') {
										setSignUpStatus('signedUp')
									}
									if (res.errorMessage) {
										setSignUpStatus(res.errorMessage)
									}
									return res
								})
							} else {
								setSignUpStatus('Password has to be at least 8 characters')
							}
						}}>
						<fieldset
							disabled={plantee.auth.signUp.isLoading}
							style={{ opacity: plantee.auth.signUp.isLoading ? 0.5 : 1 }}>
							<Input
								id="username"
								name="username"
								type="email"
								required
								iconDecoration="pencil"
								label={{
									isLabel: true,
									icon: 'user',
									text: locale === 'cs' ? 'E-mail' : 'E-mail',
								}}
							/>
							<br></br>
							<br></br>
							<br></br>
							<Input
								id="nick"
								name="nick"
								type="text"
								iconDecoration="pencil"
								required
								label={{
									isLabel: true,
									icon: 'user',
									text: locale === 'cs' ? 'Přezdívka' : 'Nickname',
								}}
								value={nick}
								onChange={(e) => {
									setNick(e.currentTarget.value)
								}}
								explanation="We need you to choose a public username, so you can comment on Planteebase and create plant profiles."
							/>
							<br></br>
							<br></br>
							<br></br>
							<Input
								id="password"
								name="password"
								type="password"
								required
								label={{
									isLabel: true,
									icon: 'lock',
									text: locale === 'cs' ? 'Heslo' : 'Password',
								}}
							/>
							<br></br>
							<br></br>
							<br></br>
							<Button type="submit" fill="green600">
								{locale === 'cs' ? 'Zaregistrovat se' : 'Sign up'}
							</Button>
						</fieldset>
					</form>
				</div>
			)}

			{signUpStatus === 'signedUp' && (
				<div>
					<p>
						{locale === 'cs'
							? 'Založení účtu proběhla úspěšně, zkontrolujte e-mailovou schránku a aktivujte śvůj účet.'
							: 'Sign up successfull, check your email for account activation.'}
					</p>
				</div>
			)}

			{signUpStatus.length > 0 && signUpStatus !== 'signedUp' && (
				<div>
					<p style={{ color: '#d73a4a' }}>{signUpStatus}</p>
				</div>
			)}

			{signedIn && (
				<form
					action=""
					method="POST"
					onSubmit={(e) => {
						e.preventDefault()
						plantee.auth.signOut.mutate()
					}}>
					<fieldset
						disabled={plantee.auth.signOut.isLoading}
						style={{ opacity: plantee.auth.signOut.isLoading ? 0.5 : 1 }}>
						<button type="submit">signOut</button>
					</fieldset>
				</form>
			)}

			{signedIn && (
				<form
					action=""
					method="POST"
					onSubmit={(e) => {
						e.preventDefault()
						plantee.changeNick.mutate(z.object({ nick: z.string() }).parse(formDataFromEvent(e)))
					}}>
					<fieldset
						disabled={plantee.changeNick.isLoading}
						style={{ opacity: plantee.changeNick.isLoading ? 0.5 : 1 }}>
						<input type="text" name="nick" placeholder="Your nick" />
						<button type="submit">
							{locale === 'cs' ? 'Změnit Přezdívka' : 'Change nickname'}
						</button>
					</fieldset>
				</form>
			)}
		</div>
	)
}
